import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./MessageBox.module.css";
import ReplyPreview from "components/ReplyPreview";
import { ContactTypes } from "pages/enums";
import { AccountContainer } from "hooks/useAccount";
import { RequirementContainer } from "hooks/useRequirement";
import { CpnwContainer } from "hooks/useCpnw";
const MessageBox = ({
  requirementType,
  message = "",
  repliedToMessage,
  onClose,
  onSend,
  onCancelReplyTo,
  canMessageStudent = false,
  canMessageEducation = false,
  canMessageHealthcare = false,
  canMessageReviewer = false,
}) => {
  const CHARACTER_LIMIT = 500;

  // 🔹 State
  const [text, setText] = useState(message);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState({});
  const [selectedRecipientLabels, setSelectedRecipientLabels] =
    useState("[Select]");

  const [recipientError, setRecipientError] = useState(false);

  const getSelectedCount = () =>
    Object.values(selectedRecipients).filter(Boolean).length;

  const hasSelected = getSelectedCount() > 0;
  // 🔹 Refs
  const dropdownRef = useRef(null);

  // 🔹 Account & Requirement Context
  const { userRoles } = AccountContainer.useContainer();
  const {
    // myAssignmentsData,
    // refetchMyAssignments,
  } = RequirementContainer.useContainer();

  // 🔹 Utility Functions
  const getAccount = (roles) => {
    if (roles.includes("STUDENT")) return "student";
    if (roles.includes("EDUCATION") || roles.includes("FACULTY"))
      return "education";
    if (roles.includes("HEALTHCARE")) return "healthcare";
    if (roles.includes("CPNW-REVIEWER")) return "reviewer";
    return null;
  };

  const getCurrentType = (type) => {
    if (type === "Education") return "Education";
    if (type === "Healthcare") return "Healthcare";
    if (type === "CPNW Clinical Passport") return "CPNW";
    return null;
  };

  // 🔹 Setup
  const currentAccount = getAccount(userRoles);
  const currentType = getCurrentType(requirementType);

  // 🔹 Effects
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const labels = Object.entries(selectedRecipients)
      .filter(([_, isChecked]) => isChecked)
      .map(([id]) => {
        const match = Object.values(recipients).find(
          (r) => r.id === Number(id)
        );
        return match?.label;
      })
      .filter(Boolean); // remove undefined/null

    if (labels.length > 0) {
      setSelectedRecipientLabels(labels.join("\n"));
    } else {
      setSelectedRecipientLabels("[Select]");
    }
  }, [selectedRecipients]);

  const handleCheckboxChange = (id) => {
    setRecipientError(false);
    const next = { ...selectedRecipients, [id]: !selectedRecipients[id] };
    if (!next[id]) delete next[id];
    setSelectedRecipients(next);
  };

  const handleSend = () => {
    if (!text.trim())
      return;

    const emptyRecipients = !selectedRecipients || Object.values(selectedRecipients).filter(Boolean).length === 0;
    if (emptyRecipients) {
      setRecipientError(true);
      setSelectedRecipientLabels("Please Select a User");
      return;
    }

    onSend(text, repliedToMessage, selectedRecipients);
    setText("");
    setDropdownOpen(false);
    setSelectedRecipients({});
    setSelectedRecipientLabels("[Select]");
    setRecipientError(false);
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText.substring(0, CHARACTER_LIMIT));
  };

  const handleClose = () => {
    onClose();
    setText("");
    setDropdownOpen(false);
    setSelectedRecipients({});
  };

  // const isWithinAssignmentWindow = (assignments) => {
  //   const now = new Date();
  //   const sixWeeksLater = new Date(now);
  //   sixWeeksLater.setDate(now.getDate() + 42);
  //   const twoWeeksAgo = new Date(now);
  //   twoWeeksAgo.setDate(now.getDate() - 14);

  //   return assignments.some((assignment) => {
  //     const start = new Date(assignment.startDate);
  //     const end = new Date(assignment.endDate);
  //     return (start >= now && start <= sixWeeksLater) || (end >= twoWeeksAgo && end <= now);
  //   });
  // };

  // const getRecipients = () => {
  //   return Object.entries(selectedRecipients)
  //     .filter(([_, isChecked]) => isChecked)
  //     .map(([id]) => Number(id));
  // };
  const isRecipientAllowed = (key) => {
    console.log(recipients);
    const allowed =
      visibilityMatrix[currentAccount]?.[currentType]?.includes(key);
    const isEnabled = recipients[key]?.enabled;
    // return allowed && isEnabled && key !== currentAccount;
    return isEnabled;
  };

  const renderRecipients = () => (
    <div className={classNames(styles.recipients, styles.optionsList)}>
      {Object.entries(recipients).map(([key, config]) => {
        if (key === currentAccount) return null;
        const isAllowed = isRecipientAllowed(key);

        return (
          <div
            key={config.id}
            style={{
              position: "relative",
              cursor: config.tooltip ? "help" : "default",
            }}
            className={styles.option}
          >
            <label
              htmlFor={config.id}
              className={styles.customCheckboxLabel}
              style={{ color: isAllowed ? "#000" : "#999" }}
            >
              <input
                type="checkbox"
                id={config.id}
                disabled={!isAllowed}
                checked={!!selectedRecipients[config.id]}
                onChange={() => handleCheckboxChange(config.id)}
                className={styles.hiddenCheckbox}
              />
              <span className={styles.customCheckbox} />
              <span className={styles.customLabel}>&nbsp;{config.label}</span>
            </label>
            {config.tooltip && (
              <span className={styles.tooltipText}>{config.tooltip}</span>
            )}
          </div>
        );
      })}
    </div>
  );

  // 🔹 Data Config
  const recipients = useMemo(() => {
    return {
      student: {
        label: "Student",
        id: ContactTypes.STUDENT,
        tooltip: "",
        enabled: canMessageStudent,
      },
      education: {
        label: "Education Coordinator",
        id: ContactTypes.EDUCATION,
        tooltip: "",
        enabled: canMessageEducation,
      },
      healthcare: {
        label: "Healthcare Coordinator",
        id: ContactTypes.HEALTHCARE,
        tooltip: canMessageHealthcare
          ? ""
          : "This healthcare organization cannot view this account because it has not yet been verified and released for access.",
        enabled: canMessageHealthcare,
      },
      reviewer: {
        label: "CPNW Reviewer",
        id: ContactTypes.CPNW_REVIEWER,
        tooltip: "",
        enabled: canMessageReviewer,
      },
    }
  }, [canMessageStudent, canMessageEducation, canMessageHealthcare, canMessageReviewer]);

  const visibilityMatrix = {
    student: {
      CPNW: ["education", "healthcare", "reviewer"],
      Education: ["education"],
      Healthcare: ["education", "healthcare"],
    },
    reviewer: {
      CPNW: ["student", "education", "healthcare"],
    },
    education: {
      CPNW: ["student", "healthcare", "reviewer"],
      Education: ["student"],
      Healthcare: ["student", "healthcare"],
    },
    healthcare: {
      CPNW: ["student", "education", "reviewer"],
      Healthcare: ["student", "education"],
    },
  };

  // 🔹 Render
  return (
    <div className={styles.container}>
      {repliedToMessage?.messageId && (
        <ReplyPreview
          isReplying
          message={repliedToMessage}
          onCancel={onCancelReplyTo}
        />
      )}

      <div className={styles.header}>
        <textarea
          className={styles.textarea}
          placeholder="Write your message here..."
          value={text}
          onChange={handleTextChange}
        />
        <div className={styles.characterCount}>
          {text.length}/{CHARACTER_LIMIT}
        </div>
        <button className={styles.closeButton} onClick={handleClose}>
          ×
        </button>
      </div>

      <div className={styles.footer}>
        <div className={styles.notifyContainer}>
          <div className={classNames(styles.notifyLabel, recipientError ? styles.errorText : undefined)}>Notify:</div>
          <div className={styles.dropdownWrap} ref={dropdownRef}>
            <div
              className={classNames(styles.trigger, recipientError ? styles.errorBorder : undefined)}
              onClick={() => setDropdownOpen((o) => !o)}
              style={{ backgroundColor: dropdownOpen ? "#f0f0f0" : "white" }}
            >
              <span className={classNames(recipientError ? styles.errorText : undefined)}>
                {selectedRecipientLabels}
              </span>
              <span
                className={classNames(styles.triangle, {
                  [styles.triangleError]: recipientError,
                })}
                style={{
                  transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
                aria-hidden="true"
              >
                ▼
              </span>
            </div>
            {dropdownOpen && renderRecipients()}
          </div>
        </div>

        <button
          className={styles.sendButton}
          onClick={handleSend}
          disabled={!text.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
};

MessageBox.propTypes = {
  requirementType: PropTypes.string,
  message: PropTypes.string,
  repliedToMessage: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onCancelReplyTo: PropTypes.func.isRequired,
};

export default MessageBox;
